import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  PseudoBox,
  Text,
} from '@chakra-ui/core'

import { useDisclosure } from '@chakra-ui/core'
import colors from 'constants/colors'
import { ChevronDown, X } from 'lucide-react'
import React from 'react'
import { useState, useRef } from 'react'
import LocaleText from 'components/LocaleText'
import { InputWithIcon } from 'components/AdminConsole/commonUI/InputWithIcon'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import useLocaleText from 'components/useLocaleText'

export type SearchableMenuItem = {
  id: string | number
  name: string
  avatar?: string
  isShowId?: boolean
  icon?: React.ReactNode
  bulletColor?: string
}

interface SearchableMenuProps {
  items: SearchableMenuItem[]
  selectedId?: string
  placeholder: string
  onSelect: (item: any) => void
  onClear?: () => void
  isShowSearch?: boolean
  isShowClearButton?: boolean
  isInvalid?: boolean
  isLocalTranslation?: boolean
  isDisabled?: boolean
  buttonProps?: Partial<ButtonProps>
}

export const SearchableMenu: React.FC<SearchableMenuProps> = ({
  items,
  placeholder,
  onSelect,
  onClear,
  selectedId,
  isShowSearch = true,
  isShowClearButton = false,
  isInvalid = false,
  isLocalTranslation = false,
  isDisabled = false,
  buttonProps,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [searchTerm, setSearchTerm] = useState('')
  const initialFocusRef = useRef<HTMLInputElement>(null)

  const filteredItems = items.filter((item) => {
    const searchTermLower = searchTerm.toLowerCase()
    const nameMatch = item.name.toLowerCase().includes(searchTermLower)
    const idMatch = item.id ? item.id.toString().toLowerCase().includes(searchTermLower) : false
    return nameMatch || idMatch
  })
  const selectedItem = items.find((item) => item.id === selectedId)
  const t_search = useLocaleText('search')

  const handleClear = () => {
    if (onClear) {
      onClear()
    }
    setSearchTerm('')
    onClose()
  }
  return (
    <Popover
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setSearchTerm('')
      }}
      initialFocusRef={initialFocusRef}
      placement="bottom-start"
      usePortal={true}
    >
      <PopoverTrigger>
        <Button
          width="100%"
          justifyContent="space-between"
          bg="white"
          borderRadius="5px"
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.100' }}
          border="1px solid"
          borderColor={isInvalid ? '#e53e3e' : 'gray.200'}
          onClick={onOpen}
          boxShadow={isInvalid ? '0 0 0 1px #e53e3e' : 'none'}
          _focus={{ boxShadow: '0 0 0 1px black' }}
          isDisabled={isDisabled}
          {...buttonProps}
        >
          <Flex align="center" width="100%" overflow="hidden">
            <Box flex="1" overflow="hidden" minW={0}>
              {!selectedItem ? (
                <Text fontSize="14px" fontWeight="400" isTruncated>
                  <LocaleText text={placeholder} isLocalUse={isLocalTranslation} />
                </Text>
              ) : (
                <Flex align="center" style={{ gap: '6px' }} overflow="hidden">
                  {selectedItem?.bulletColor && (
                    <Box w={3} h={3} borderRadius="full" bg={selectedItem.bulletColor} flexShrink={0} />
                  )}
                  {selectedItem.avatar && (
                    <Text fontSize="14px" fontWeight="400" flexShrink={0}>
                      {selectedItem.avatar}
                    </Text>
                  )}
                  {selectedItem?.icon && <Box flexShrink={0}>{selectedItem.icon}</Box>}
                  <Text fontSize="14px" fontWeight="400" isTruncated>
                    <LocaleText text={selectedItem.name} isLocalUse={isLocalTranslation} />
                  </Text>
                </Flex>
              )}
            </Box>
            <Box flexShrink={0} mx={2}>
              <ChevronDown size={20} />
            </Box>
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        width="max-content"
        minWidth="200px"
        maxWidth={{ base: '300px', lg: '100%' }}
        isTruncated
        bg="white"
        placement="bottom-start"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="lg"
        zIndex={99999}
        _focus={{ outline: 'none' }}
        borderRadius="5px"
        style={{
          background: 'white',
          opacity: 1,
          position: 'absolute',
          zIndex: 99999,
        }}
      >
        <PopoverBody
          p={2}
          borderRadius="5px"
          overflow="hidden"
          style={{
            background: 'white',
            opacity: 1,
          }}
        >
          {isShowSearch && (
            <InputWithIcon
              borderRadius="5px"
              _focus={{ borderColor: colors.greyLight }}
              ref={initialFocusRef}
              placeholder={t_search}
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              size="sm"
              mb={2}
              autoComplete="off"
            />
          )}

          {isShowClearButton && selectedItem && (
            <NoFocusButton
              size="sm"
              variant="ghost"
              width="100%"
              borderRadius="5px"
              justifyContent="flex-start"
              style={{ columnGap: '10px' }}
              onClick={handleClear}
              _hover={{ bg: 'gray.100' }}
            >
              <X size={16} />
              <Text fontSize="12px">
                <LocaleText text="clear_filter" />
              </Text>
            </NoFocusButton>
          )}

          <Box maxH="300px" overflowY="auto" borderRadius="5px">
            {filteredItems.map((item) => (
              <PseudoBox
                key={item.id}
                p={2}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onClick={() => {
                  onSelect(item)
                  setSearchTerm('')
                  onClose()
                }}
              >
                <Flex align="center" fontSize={{ base: '12px', md: '14px' }} style={{ gap: '8px' }}>
                  {item.bulletColor && <Box w={3} h={3} borderRadius="full" bg={item.bulletColor} flexShrink={0} />}
                  {item.avatar && <Box>{item.avatar}</Box>}
                  {item.icon && <Box>{item.icon}</Box>}
                  <Text isTruncated>
                    <LocaleText text={item.name} isLocalUse={isLocalTranslation} />
                  </Text>
                  <Text isTruncated>{item.id && item.isShowId ? `(${item.id})` : ''}</Text>
                </Flex>
              </PseudoBox>
            ))}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
